"use client";

import React from "react";

export interface ComparisonItem {
  icon: React.ReactNode;
  title: string;
  items: string[];
}

export interface ComparisonSectionProps {
  title: string;
  subtitle?: string;
  leftColumn: ComparisonItem;
  rightColumn: ComparisonItem;
  bgColor?: string;
}

export function ComparisonSection({
  title,
  subtitle,
  leftColumn,
  rightColumn,
  bgColor = "bg-white"
}: ComparisonSectionProps) {
  return (
    <section className={`py-16 md:py-20 ${bgColor}`}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          {subtitle && (
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              {subtitle}
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Left Column */}
          <div className="bg-gray-50 rounded-2xl p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-gray-200 rounded-xl flex items-center justify-center">
                {leftColumn.icon}
              </div>
              <h3 className="text-2xl font-semibold text-gray-900">
                {leftColumn.title}
              </h3>
            </div>
            <ul className="space-y-4">
              {leftColumn.items.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right Column */}
          <div className="bg-primary-50 rounded-2xl p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                {rightColumn.icon}
              </div>
              <h3 className="text-2xl font-semibold text-gray-900">
                {rightColumn.title}
              </h3>
            </div>
            <ul className="space-y-4">
              {rightColumn.items.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}